import React, { Fragment, useEffect } from "react";
import { Listbox, Transition } from "@headlessui/react";
import classNames from "classnames";

import Icon, { IconType } from "../Icon/Icon";
import Button from "../Button/Button";
import TextButton from "../TextButton/TextButton";
import styles from "./Dropdown.module.scss";

export type DropdownOption = {
  label: string;
  value: any;
  icon?: IconType;
  color?: "black" | "red";
  disabled?: boolean;
};

interface Props {
  dataQA: string;
  options: DropdownOption[];
  maxItemsBeforeScroll?: number;
  maxWidth?: number;
  multiSelect?: boolean;
  showHalfOption?: boolean;
  placeholder?: string;
  separator?: boolean;
  customTriggerComponent?: React.ReactNode;
  className?: string;
  onClick: (selection: DropdownOption | DropdownOption[] | null) => void;
  disabled?: boolean;
  cancelButtonLabel?: string;
  doneButtonLabel?: string;
  selection: DropdownOption | DropdownOption[] | null;
  setSelection: (selection: DropdownOption | DropdownOption[] | null) => void;
}

const Dropdown: React.FC<Props> = ({
  dataQA,
  options,
  maxItemsBeforeScroll,
  maxWidth = 240,
  multiSelect,
  showHalfOption,
  placeholder = "Select",
  separator,
  customTriggerComponent,
  className,
  onClick,
  disabled = false,
  cancelButtonLabel = "Cancel",
  doneButtonLabel = "Done",
  selection,
  setSelection,
}) => {
  const handleOnChange = (value: DropdownOption | DropdownOption[]) => {
    setSelection(value);

    // Only return the selection on change if it's a single select dropdown. If it's a multiselect, the selection will be returned once the done button at the bottom of the list is clicked.
    if (!multiSelect) {
      onClick(value);
    }
  };

  const selectionLabel =
    selection && "length" in selection
      ? selection.map((option) => option.label).join(", ")
      : selection?.label;

  let maxHeight = "none";

  if (maxItemsBeforeScroll && !showHalfOption) {
    maxHeight = `${maxItemsBeforeScroll * 48}px`;
  }

  if (maxItemsBeforeScroll && showHalfOption) {
    maxHeight = `${maxItemsBeforeScroll * 48 + 24}px`;
  }

  // Just so the selection state gets reset when switching between multiselect or single select. Realistically this should only ever be an issue when testing it out in storybook, so probably can just be removed.
  useEffect(() => {
    if (multiSelect) {
      setSelection([]);
    } else {
      setSelection(null);
    }
  }, [multiSelect, setSelection]);

  return (
    <div
      data-qa={dataQA}
      className={classNames(styles.container, className, {
        [styles.disabled]: disabled,
      })}
      style={{ maxWidth: `${maxWidth}px` }}
    >
      <Listbox
        value={selection}
        onChange={handleOnChange}
        multiple={multiSelect}
      >
        {customTriggerComponent ? (
          <Listbox.Button as={Fragment}>
            {customTriggerComponent}
          </Listbox.Button>
        ) : (
          <Listbox.Button className={styles.button}>
            {selectionLabel || placeholder}
            <Icon dataQA="icon" type="ChevronDownIcon" />
          </Listbox.Button>
        )}
        <div className={styles.panel}>
          <Transition
            enter={styles.enter}
            enterFrom={styles.enterFrom}
            enterTo={styles.enterTo}
            leave={styles.leave}
            leaveFrom={styles.leaveFrom}
            leaveTo={styles.leaveTo}
            className={styles.transitionElement}
          >
            <Listbox.Options
              data-qa="dropdown-list"
              style={{ maxHeight }}
              className={styles.dropdown}
            >
              {options.map((option, index) => (
                <Listbox.Option
                  data-qa="dropdown-list-item"
                  key={option.label}
                  as={Fragment}
                  value={option}
                  disabled={option.disabled}
                >
                  {({ selected, disabled }) => (
                    <li
                      className={classNames(
                        styles.dropdownItem,
                        styles[option.color || "black"],
                        {
                          [styles.selected]: selected,
                          [styles.disabled]: disabled,
                          [styles.separator]:
                            separator && index + 3 === options.length,
                        },
                      )}
                    >
                      {option.icon && !multiSelect && (
                        <Icon
                          dataQA="icon"
                          type={option.icon}
                          width={16}
                          height={16}
                          className={styles.icon}
                        />
                      )}
                      {multiSelect && !selected && (
                        <Icon
                          dataQA="icon"
                          type="CheckBoxOutlineIcon"
                          width={16}
                          height={16}
                          className={classNames(styles.icon, styles.greyIcon)}
                        />
                      )}
                      {multiSelect && selected && (
                        <Icon
                          dataQA="icon"
                          type="CheckBoxIcon"
                          width={16}
                          height={16}
                          className={styles.icon}
                        />
                      )}
                      <span>{option.label}</span>
                    </li>
                  )}
                </Listbox.Option>
              ))}
            </Listbox.Options>
            {multiSelect && (
              <div className={styles.buttonWrapper}>
                <TextButton
                  dataQA="cancel-button"
                  color="black"
                  onClick={() => setSelection([])}
                >
                  {cancelButtonLabel}
                </TextButton>
                <Button
                  dataQA="done-button"
                  color="black"
                  onClick={() => onClick(selection)}
                >
                  {doneButtonLabel}
                </Button>
              </div>
            )}
          </Transition>
          <div className={styles.spacer} />
        </div>
      </Listbox>
    </div>
  );
};

export default Dropdown;
